import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { FiArrowRight } from 'react-icons/fi';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { ArrowSmallLeftSvg } from '../../../assets/svg-icon-components/ArrowSmallLeftSvg';
import { CalendarSvg } from '../../../assets/svg-icon-components/CalendarSvg';
import { ClockSvg } from '../../../assets/svg-icon-components/ClockSvg';
import { PlayCircleSvg } from '../../../assets/svg-icon-components/PlayCircleSvg';
import { TimeCheckSvg } from '../../../assets/svg-icon-components/TimeCheckSvg';
import { UsersSvg } from '../../../assets/svg-icon-components/UsersSvg';
import { getTimezone } from '../../../util/date-utilities';
import { getMuxHlsPlayback } from '../../../util/mux-utilities';
import { useUserRoles } from '../../../util/use-user-role';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { Can } from '../../core/rbac/Can';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhCard } from '../../core/RhCard';
import { RhCardListItem } from '../../core/RhCardListItem';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { RhFormGrid, RhFormGridColumn } from '../../core/RhFormGrid';
import { RhIconText } from '../../core/RhIconText';
import { RhImage } from '../../core/RhImage';
import { RhMoreDropdown } from '../../core/RhMoreDropdown';
import { RhRawButton } from '../../core/RhRawButton';
import { RhTag } from '../../core/RhTag';
import { SizedBox } from '../../core/SizedBox';
import { colors, Description, H1, H2, H3 } from '../../core/Styles';
import { ViewEventProps } from '../containers/ViewEventContainer';

export const ViewEventScreen: React.FC<ViewEventProps> = props => {
  // It's counter-intuitive to put this on the container
  const { roles } = useUserRoles();

  return (
    <DashboardWrapper>
      <DashboardHeader
        title={
          <RhRawButton onClick={props.userClickedBack}>
            <Title>
              <ArrowSmallLeftSvg fontSize={30} fill={colors.normal} />
              <H1>Events</H1>
            </Title>
          </RhRawButton>
        }
        actions={[
          props.isStartShown ? (
            <Can
              key="start-event"
              role={roles?.[0]}
              perform="update:events"
              yes={
                <RhButton
                  title="Start Event"
                  suffixSvg={PlayCircleSvg}
                  iconSize={18}
                  onClick={props.userClickedStartEvent}
                  loading={props.isLoadingEvent}
                  disabled={props.isStartDisabled}
                />
              }
            />
          ) : (
            <Can
              key="end-event"
              role={roles?.[0]}
              perform="update:events"
              yes={
                <RhButton
                  title="End Event"
                  suffixSvg={PlayCircleSvg}
                  iconSize={18}
                  loading={props.isLoadingEvent}
                  onClick={props.userClickedEndEvent}
                />
              }
            />
          ),
          <Can
            key="edit-event"
            role={roles?.[0]}
            perform="update:events"
            yes={
              <RhButton
                title="Edit Event"
                onClick={props.userClickedEditEvent}
                loading={props.isLoadingEvent}
                disabled={props.isEditDisabled}
              />
            }
          />,
          <RhMoreDropdown
            key="more-dropdown"
            deletePermission="delete:events"
            isDeleteDisabled={props.isDeleteDisabled}
            userClickedDelete={props.userToggledConfirmDeleteModal}
          />,
        ]}
      />
      <RhFormGrid>
        <RhFormGridColumn>
          <RhCard>
            <H1>{props.event?.title}</H1>
            <SizedBox height={16} />
            <Description>{props.event?.description}</Description>
          </RhCard>
          <SchedulingGrid>
            <RhCard>
              {/* Scheduled Date  */}
              <H2>Scheduled Date</H2>
              <SizedBox height={24} />
              <DateInputDiv>
                <CalendarSvg width={20} height={20} />
                {props.event?.startTime && (
                  <div>
                    <H1 style={{ lineHeight: `24px` }} color={colors.primary}>
                      {dayjs(props.event.startTime).format('MM/DD/YYYY')}
                    </H1>
                    <SizedBox height={4} />
                    <H2 color={colors.grey}>{dayjs(props.event.startTime).format('dddd').toUpperCase()}</H2>
                  </div>
                )}
              </DateInputDiv>
            </RhCard>
            {/* Start Time  */}
            <RhCard>
              <H2>Start Time</H2>
              <SizedBox height={24} />
              <DateInputDiv>
                <ClockSvg width={20} height={20} />
                {props.event?.startTime && (
                  <div>
                    <H1 style={{ lineHeight: `24px` }} color={colors.primary}>
                      {dayjs(props.event.startTime).format('hh:mm A')}
                    </H1>
                    <SizedBox height={4} />
                    <H2 color={colors.grey}>{getTimezone(new Date(props.event.startTime))}</H2>
                  </div>
                )}
              </DateInputDiv>
            </RhCard>
            {/* End Time  */}
            <RhCard>
              <H2>End Time</H2>
              <SizedBox height={24} />
              <DateInputDiv>
                <ClockSvg width={20} height={20} />
                {props.event?.endTime && (
                  <div>
                    <H1 style={{ lineHeight: `24px` }} color={colors.primary}>
                      {dayjs(props.event.endTime).format('hh:mm A')}
                    </H1>
                    <SizedBox height={4} />
                    <H2 color={colors.grey}>{getTimezone(new Date(props.event.endTime))}</H2>
                  </div>
                )}
              </DateInputDiv>
            </RhCard>
            {/* People Registered */}
            <RhCard>
              <H2>People Registered</H2>
              <SizedBox height={24} />
              <DateInputDiv>
                <UsersSvg width={20} height={20} />
                <H1 style={{ lineHeight: `24px` }} color={colors.primary}>
                  {/* TODO: update people registered */}
                  {/* {props.event.peopleRegistered} */}0
                </H1>
              </DateInputDiv>
            </RhCard>
          </SchedulingGrid>
          <RhCard>
            <H2>Study Program</H2>
            {props.event?.theProgram && (
              <>
                <SizedBox height={20} />
                <RhCardListItem
                  imageMode="minirect"
                  imageUrl={props.event.theProgram.imageUrl}
                  title={props.event.theProgram.title}
                  author={_.startCase(props.event.theProgram.authorName) || '-'}
                  authorImage={props.event.theProgram.authorImage}
                  dateUpdated={props.event.theProgram.dateUpdated}
                  description={props.event.theProgram.description}
                  topRightActions={
                    <RhButton
                      mode={RhButtonMode.text}
                      title="Go to Study"
                      suffixIcon={FiArrowRight}
                      onClick={props.userClickedGoToStudy}
                    />
                  }
                />
              </>
            )}
          </RhCard>
          <RhCard>
            <H2>Vimeo Event URL</H2>
            <SizedBox height={12} />
            <a
              className="font-bold text-blue-600 hover:underline"
              target="_blank"
              rel="noreferrer noopener"
              href={props.event?.vimeoEventUrl || ''}>
              {props.event?.vimeoEventUrl || '-'}
            </a>
          </RhCard>
        </RhFormGridColumn>
        <RhFormGridColumn>
          <RhCard mode="tiled">
            <H2>Publishing Status</H2>
            <SizedBox height={12} />
            <RhIconText icon={TimeCheckSvg} text={_.startCase(props.event?.publishingStatus) || '-'} />
          </RhCard>
          <RhCard mode="tiled">
            <H2>Event Leader</H2>
            {props.event?.leader && (
              <React.Fragment>
                <SizedBox height={12} />
                {/* TODO: update event leader image */}
                <RhIconText
                  iconOverride={<RhImage src="" width={42} height={42} radius={21} />}
                  textOverride={<H3>{_.startCase(props.event.leader.name)}</H3>}
                />
              </React.Fragment>
            )}
          </RhCard>
          <RhCard mode="tiled">
            <H2>Feature Image</H2>
            {!!props.event?.featuredImage.url && (
              <>
                <SizedBox height={12} />
                <RhImage mode="full" src={props.event.featuredImage.url} />
              </>
            )}
          </RhCard>
          <RhCard mode="tiled">
            <H2>Introduction Video</H2>
            {!!props.event?.introMuxId && (
              <PlayerDiv>
                <ReactPlayer
                  url={getMuxHlsPlayback(props.event.introMuxId)}
                  controls={true}
                  width="100%"
                  height="100%"
                  style={styles.player}
                />
              </PlayerDiv>
            )}
          </RhCard>
          <RhCard mode="tiled">
            <H2>Tags</H2>
            {!!props.event?.tags.length && (
              <Tags>
                {props.event.tags.map(tag => (
                  <RhTag key={tag} editable={false} title={tag} className="p-1" />
                ))}
              </Tags>
            )}
          </RhCard>
          <RhCard mode="tiled">
            <H2>Taxonimic Category</H2>
          </RhCard>
        </RhFormGridColumn>
      </RhFormGrid>

      {/* Delete Confirmation Modal */}
      <RhConfirmModal
        description="Are you sure you want to delete this event?"
        isOpen={props.isConfirmDeleteModalOpen}
        isCloseDisabled={props.isDeletingEvent}
        onClose={() => {
          if (!props.isDeletingEvent) {
            props.userToggledConfirmDeleteModal();
          }
        }}
        isOkDisabled={props.isDeletingEvent}
        onOk={props.userClickedOkToDelete}
      />
    </DashboardWrapper>
  );
};

const styles = {
  deleteConfirmModal: {
    minWidth: 'unset',
  },
  deleteConfirmFooter: {
    justifyContent: 'flex-end',
  },
  deleteConfirmOk: {
    fontWeight: 900,
    fontSize: 16,
  },
  deleteConfirmCancel: {
    fontWeight: 600,
    fontSize: 16,
  },
  player: {
    borderRadius: 16,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
  } as React.CSSProperties,
};

const Title = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 8px;
  }
`;

const SchedulingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-gap: 20px;
`;

const DateInputDiv = styled.div`
  display: flex;
  & > :not(:last-child) {
    margin-right: 18px;
  }
`;

const Tags = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  & > * {
    margin-right: 8px;
    margin-bottom: 8px;
  }
`;

const PlayerDiv = styled.div`
  width: 100%;
  margin-top: 12px;
  position: relative;
  // fixed aspect ratio box
  // see: https://github.com/cookpete/react-player#responsive-player
  padding-top: 56.25%;
`;
